"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Flame, Zap, Shield, Link as ChainIcon } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LeDiablePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Le Diable</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/30 to-black/50 rounded-xl p-8 mb-8 border border-red-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-le-diable.svg"
                  alt="Le Diable Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-red-900/50 to-black/70 border border-red-600/40 flex-col items-center justify-center text-red-300">
                  <span className="text-3xl font-bold mb-2">XV</span>
                  <span className="text-lg font-semibold">LE DIABLE</span>
                  <span className="text-sm text-center mt-2">The Devil</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-black rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XV</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Le Diable</h1>
                  <p className="text-xl text-red-400">Bağlayıcılar / Tutku ve Gölge Benlik</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde on beşinci kart. Maddi dünyaya aşırı bağlılık, tutsakhık ve içsel
                korkularımızı temsil eder. Gerçek özgürlüğü bulma yolculuğunda karşılaştığımız engelleri
                ve illüzyonları gösterir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Bağlayıcılar</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Materyalizm</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Gölge Benlik</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">İllüzyon</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Flame className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gerçeğin Farkındalığı</h3>
                  <p className="text-slate-400 text-sm">İçindeki gizli arzular ve korkularla yüzleşme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgürlüğe Doğru</h3>
                  <p className="text-slate-400 text-sm">Bağımlılıkları fark etme ve onlardan kurtulma süreci.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlülük</h3>
                  <p className="text-slate-400 text-sm">Maddi dünya ve tutkular üzerinde hakim olma kabiliyeti.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dönüşüm</h3>
                  <p className="text-slate-400 text-sm">İçsel karanlığı aydınlığa çevirme süreci.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <ChainIcon className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bağımlılık</h3>
                  <p className="text-slate-400 text-sm">Maddi nesne, kişi ya da alışkanlığa bağımlı kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kontrolsüzlük</h3>
                  <p className="text-slate-400 text-sm">Tutkular ve obsesyonlar tarafından yönetilme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manipülasyon</h3>
                  <p className="text-slate-400 text-sm">Başkaları tarafından kontrol edilme ya da kullanılma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Korku Besleme</h3>
                  <p className="text-slate-400 text-sm">Kendi korkularımızla kendimize zarar verme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👹</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Şeytani Figür</h3>
              <p className="text-slate-400 text-sm">Karmaşıklık, tutku ve kontrolsüz enerjilerin sembolü.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <ChainIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Zincirler</h3>
              <p className="text-slate-400 text-sm">Bağımlılıklar, kısıtlamalar ve irademizi engelleyen faktörler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👫</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Tutuk Çift</h3>
              <p className="text-slate-400 text-sm">Maddi bağlarla tutsak kalan insanlık durumu.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ateş</h3>
              <p className="text-slate-400 text-sm">Tutku, arzu ve ham enerji. Dönüştürücü güç potansiyeli.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⛰️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dağ</h3>
              <p className="text-slate-400 text-sm">Aşılması güç görünen engeller ve içsel korkular.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Karanlık</h3>
              <p className="text-slate-400 text-sm">Bilinçsiz alan, gizli korkular ve bastırılmış duygular.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Toksik ilişki kalıpları</li>
                <li>• Aşırı bağımlılık</li>
                <li>• Kıskançlık ve kontrol</li>
                <li>• Cinsel obsesyonlar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Para odaklı obsesyon</li>
                <li>• İş yerinde manipülasyon</li>
                <li>• Etik dışı uygulamalar</li>
                <li>• Kariyerizm ve egoist yaklaşım</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Flame className="h-5 w-5 text-red-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İçsel korkularla yüzleşme</li>
                <li>• Bağımlılık kalıpları</li>
                <li>• Gölge benlikle çalışmak</li>
                <li>• Özgürleşme süreci</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İllüzyonların aşılması</li>
                <li>• Ego ve maddi dünyanın aşılması</li>
                <li>• İçsel aydınlanma yolculuğu</li>
                <li>• Karmik bağların çözme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/temperance"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Tempérance
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-maison-dieu"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Sonraki: La Maison Dieu
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}